import {
  CreateFieldRequest,
  FieldDataType,
  FieldResponse,
  NumericFieldResponse,
  ResourceFieldResponse,
  SingleSelectFieldResponse,
  UpdateFieldRequest,
} from 'mapistry-shared';
import React, { useCallback, useMemo, useState } from 'react';
import {
  BaseFieldWizardModal,
  getError,
  mapFormValuesToBaseRequestDto,
} from './BaseFieldWizardModal';
import { DataTypeFactory } from './DataTypeFactory';
import { FieldDataTypeSelector } from './FieldDataTypeSelector';

function mapFormValuesToRequestDto(
  values: Partial<FieldResponse>,
): CreateFieldRequest | UpdateFieldRequest {
  const baseRequestDto = mapFormValuesToBaseRequestDto(values);

  switch (values.type) {
    case FieldDataType.NUMERIC: {
      const { units } = values as Partial<NumericFieldResponse>;
      return { ...baseRequestDto, units: units || null };
    }
    case FieldDataType.SINGLE_SELECT: {
      const { options } = values as Partial<SingleSelectFieldResponse>;
      if (!options) throw getError(values);
      return { ...baseRequestDto, options };
    }
    case FieldDataType.RESOURCE: {
      const { resourceTypeId } = values as Partial<ResourceFieldResponse>;
      if (!resourceTypeId) throw getError(values);
      return { ...baseRequestDto, resourceTypeId };
    }
    default:
      return baseRequestDto;
  }
}

interface FieldDataTypeWizardModalProps {
  availableFieldTypes: React.ComponentProps<
    typeof FieldDataTypeSelector
  >['availableFieldTypes'];
  field?: FieldResponse;
  fieldTypeName?: string;
  isRequiredHelpText?: string;
  onClose: () => void;
  onSubmit: (values: CreateFieldRequest | UpdateFieldRequest) => Promise<void>;
}

export function FieldDataTypeWizardModal({
  availableFieldTypes,
  field: inboundField,
  fieldTypeName = 'field',
  isRequiredHelpText,
  onClose,
  onSubmit: onSubmitProp,
}: FieldDataTypeWizardModalProps) {
  const isEditing = !!inboundField;

  const [field, setField] = useState<Partial<FieldResponse>>(
    inboundField || {},
  );

  const wizardPages = useMemo(
    () => [
      <FieldDataTypeSelector
        availableFieldTypes={availableFieldTypes}
        fieldTypeName={fieldTypeName}
      />,
      <DataTypeFactory
        dataType={field.type}
        fieldTypeName={fieldTypeName}
        isRequiredHelpText={isRequiredHelpText}
        isEditing={isEditing}
      />,
    ],
    [
      availableFieldTypes,
      field.type,
      fieldTypeName,
      isEditing,
      isRequiredHelpText,
    ],
  );

  const isUpdateRequest = (
    x: CreateFieldRequest | UpdateFieldRequest | Partial<FieldResponse>,
  ): x is UpdateFieldRequest => 'id' in x && x.id != null;

  const onNextPage = useCallback(
    async (formValues: Partial<FieldResponse>, pageNumber) => {
      // Reset values if a user goes back and selects a different field type.
      // Note: we are not allowing to change type after a field is created
      if (pageNumber === 0 && !isUpdateRequest(formValues)) {
        formValues.type === field.type
          ? setField({ ...field, ...formValues })
          : setField({ type: formValues.type });
      }
    },
    [field],
  );

  const onSubmit = useCallback(
    async (formValues: Partial<FieldResponse>) => {
      const values = mapFormValuesToRequestDto(formValues);
      await onSubmitProp(values);
    },
    [onSubmitProp],
  );

  return (
    <BaseFieldWizardModal
      field={field}
      fieldTypeName={fieldTypeName}
      onClose={onClose}
      onNextPage={onNextPage}
      onPageSubmit={onSubmit}
      showBackButton={!isEditing}
      startPage={isEditing ? 1 : 0}
      wizardPages={wizardPages}
    />
  );
}
